import { useForm } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { FormSection } from '@/components/backoffice/FormSection'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { EntitySummary } from '@/types'

type Props = { entity: EntitySummary; support_type: string }

const SUPPORT_FIELDS: Record<string, { key: string; label: string; type: 'number' | 'boolean' | 'text' }[]> = {
  owner: [
    { key: 'min_count', label: 'Min Count', type: 'number' },
    { key: 'max_count', label: 'Max Count', type: 'number' },
    { key: 'public_disclosure', label: 'Public Disclosure', type: 'boolean' },
  ],
  director: [
    { key: 'requires_board_chair', label: 'Requires Board Chair', type: 'boolean' },
    { key: 'min_count', label: 'Min Count', type: 'number' },
    { key: 'max_count', label: 'Max Count', type: 'number' },
    { key: 'public_disclosure', label: 'Public Disclosure', type: 'boolean' },
  ],
  president: [{ key: 'required', label: 'Required', type: 'boolean' }],
  secretary: [{ key: 'required', label: 'Required', type: 'boolean' }],
  treasurer: [{ key: 'required', label: 'Required', type: 'boolean' }],
  manager: [
    { key: 'allows_member_managed', label: 'Allows Member Managed', type: 'boolean' },
    { key: 'allows_manager_managed', label: 'Allows Manager Managed', type: 'boolean' },
    { key: 'min_count', label: 'Min Count', type: 'number' },
    { key: 'max_count', label: 'Max Count', type: 'number' },
  ],
  trustee: [
    { key: 'min_count', label: 'Min Count', type: 'number' },
    { key: 'max_count', label: 'Max Count', type: 'number' },
    { key: 'public_disclosure', label: 'Public Disclosure', type: 'boolean' },
  ],
  incorporator: [{ key: 'required', label: 'Required', type: 'boolean' }],
  registered_agent: [
    { key: 'allows_individual', label: 'Allows Individual', type: 'boolean' },
    { key: 'allows_entity', label: 'Allows Entity', type: 'boolean' },
    { key: 'required', label: 'Required', type: 'boolean' },
  ],
  capital: [
    { key: 'min_cents', label: 'Min (cents)', type: 'number' },
    { key: 'max_cents', label: 'Max (cents)', type: 'number' },
    { key: 'recommended_cents', label: 'Recommended (cents)', type: 'number' },
  ],
  formation_name_option: [
    { key: 'label', label: 'Label', type: 'text' },
    { key: 'suffix', label: 'Suffix', type: 'text' },
    { key: 'position', label: 'Position', type: 'number' },
  ],
}

function supportLabel(type: string): string {
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export default function SupportNew({ entity, support_type }: Props) {
  const fields = SUPPORT_FIELDS[support_type] || []
  const paramKey = support_type === 'formation_name_option' ? 'formation_name_option' : `${support_type}_support`
  const urlPath = support_type === 'formation_name_option' ? 'formation_name_options' : `${support_type}_supports`
  const initialNested: Record<string, unknown> = {}
  fields.forEach(f => { initialNested[f.key] = f.type === 'boolean' ? false : '' })

  const { data, setData, post, processing } = useForm({ [paramKey]: initialNested as Record<string, string | number | boolean> })
  const supportData = data[paramKey] || {}
  const setSupportField = (key: string, value: unknown) => setData(paramKey, { ...supportData, [key]: value as string | number | boolean })

  return (
    <BackofficeLayout title={`Add ${supportLabel(support_type)} Support`} breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Entities', href: '/backoffice/entities' }, { label: entity.name, href: `/backoffice/entities/${entity.id}` }, { label: `Add ${supportLabel(support_type)} Support` }]}>
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Add {supportLabel(support_type)} Support</h1>
          <p className="text-sm text-muted-foreground">{entity.name}</p>
        </div>

        <form onSubmit={e => { e.preventDefault(); post(`/backoffice/entities/${entity.id}/${urlPath}`) }} className="max-w-sm space-y-6">
          <FormSection title="Configuration">
            <div className="space-y-3">
              {fields.map(field => (
                <div key={field.key}>
                  {field.type === 'boolean' ? (
                    <div className="flex items-center gap-2">
                      <Checkbox id={field.key} checked={!!supportData[field.key]} onCheckedChange={c => setSupportField(field.key, !!c)} />
                      <label htmlFor={field.key} className="text-sm">{field.label}</label>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">{field.label}</label>
                      <Input className="h-8 text-sm" type={field.type === 'text' ? 'text' : 'number'} value={supportData[field.key] as string} onChange={e => setSupportField(field.key, field.type === 'number' ? (e.target.value === '' ? null : parseInt(e.target.value)) : e.target.value)} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </FormSection>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={processing} className="h-8 text-[13px]">Add Support</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => window.history.back()} className="h-8 text-[13px]">Cancel</Button>
          </div>
        </form>
      </div>
    </BackofficeLayout>
  )
}
